/**
 * ConfidenceIndicator - Scientific gauge-style confidence display
 *
 * Visual confidence badge using traffic light colors:
 * - Green (>80%): High confidence, auto-importable
 * - Yellow (70-80%): Medium confidence, threshold met
 * - Red (<70%): Low confidence, needs review
 */

import { cn } from '@/lib/cn';

interface ConfidenceIndicatorProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

type ConfidenceLevel = 'high' | 'medium' | 'low';

function getConfidenceLevel(score: number): ConfidenceLevel {
  if (score >= 80) return 'high';
  if (score >= 70) return 'medium';
  return 'low';
}

const levelConfig: Record<ConfidenceLevel, { color: string; bg: string; ring: string; label: string }> = {
  high: {
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    ring: 'ring-emerald-500/30',
    label: 'High',
  },
  medium: {
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    ring: 'ring-amber-500/30',
    label: 'Review',
  },
  low: {
    color: 'text-red-700',
    bg: 'bg-red-50',
    ring: 'ring-red-500/30',
    label: 'Low',
  },
};

const sizeConfig = {
  sm: {
    container: 'px-1.5 py-0.5 text-[10px]',
    dot: 'w-1.5 h-1.5',
  },
  md: {
    container: 'px-2 py-1 text-xs',
    dot: 'w-2 h-2',
  },
  lg: {
    container: 'px-2.5 py-1.5 text-sm',
    dot: 'w-2.5 h-2.5',
  },
};

export function ConfidenceIndicator({
  score,
  size = 'md',
  showLabel = false,
  className,
}: ConfidenceIndicatorProps) {
  const level = getConfidenceLevel(score);
  const config = levelConfig[level];
  const sizeStyles = sizeConfig[size];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md font-mono font-medium',
        'ring-1',
        config.bg,
        config.color,
        config.ring,
        sizeStyles.container,
        className
      )}
    >
      {/* Pulsing indicator dot for low confidence */}
      <span
        className={cn(
          'rounded-full shrink-0',
          sizeStyles.dot,
          level === 'high' && 'bg-emerald-500',
          level === 'medium' && 'bg-amber-500',
          level === 'low' && 'bg-red-500 animate-pulse'
        )}
      />
      <span>{score.toFixed(0)}%</span>
      {showLabel && (
        <span className="opacity-70 font-normal">{config.label}</span>
      )}
    </div>
  );
}

/**
 * ConfidenceBar - Horizontal progress-style confidence bar
 */
interface ConfidenceBarProps {
  score: number;
  className?: string;
}

export function ConfidenceBar({ score, className }: ConfidenceBarProps) {
  const level = getConfidenceLevel(score);

  return (
    <div className={cn('relative', className)}>
      {/* Background track */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        {/* Fill */}
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            level === 'high' && 'bg-emerald-500',
            level === 'medium' && 'bg-amber-500',
            level === 'low' && 'bg-red-500'
          )}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>
      {/* Threshold markers */}
      <div className="absolute inset-0 flex">
        <div className="w-[70%] border-r border-gray-300/50" title="70% threshold" />
        <div className="w-[10%] border-r border-gray-300/50" title="80% threshold" />
      </div>
    </div>
  );
}

/**
 * FieldConfidenceIcon - Small inline confidence indicator for form fields
 */
interface FieldConfidenceIconProps {
  score?: number;
  hint?: string;
  className?: string;
}

export function FieldConfidenceIcon({ score, hint, className }: FieldConfidenceIconProps) {
  if (score === undefined) return null;

  const level = getConfidenceLevel(score);

  return (
    <div
      className={cn('group relative', className)}
      title={hint || `${score.toFixed(0)}% confidence`}
    >
      <span
        className={cn(
          'inline-block w-2 h-2 rounded-full cursor-help',
          level === 'high' && 'bg-emerald-500',
          level === 'medium' && 'bg-amber-500 animate-pulse',
          level === 'low' && 'bg-red-500 animate-pulse'
        )}
      />
      {hint && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-900 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
          {hint}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
}
